import queryString, { ParsedQuery } from 'query-string';
import { LocationQuery } from 'vue-router';
import moment from 'moment';
import { IFilter, QueryRecord } from '@/types';
import { claimQueryKey } from '@/data';
import { IRequestError } from '@/types/error';

export const createQueryString = (query: QueryRecord) =>
  Object.entries(query)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

export const getPathFromUrl = (url: string): string => url.split('?')[0];

export const getQueryFromUrl = (url: string): ParsedQuery => queryString.parse(url.split('?')[1]);

/**
 * Maps and formats Vue router's query parameters to the equivalents needed for the backend API.
 *
 *   * Mapping: For example:`supplier` in Vue becomes `supplier_id`.  There are others.  See `enum claimQueryKey` for more.
 *   * Formatting: `dateDeliveredBegin` and `dateDeliveredEnd` to Unix millisecond format.
 *   * Filters out empty values
 *
 * @param filter Query string, as used by the Vue router
 * @returns Query string, formatted for the backend API
 */
export const generateClaimQuery = (filter: LocationQuery) => {
  const claimQuery: LocationQuery = {};

  for (const queryKey in filter) {
    if (filter[queryKey]) {
      const queryName: string | undefined = claimQueryKey[queryKey as keyof IFilter];

      if (queryName !== undefined) {
        // the date fields require epoc time format before sending back to server
        if (queryKey === 'dateDeliveredBegin' || queryKey === 'dateDeliveredEnd') {
          claimQuery[queryName] = moment(filter[queryKey]?.toString()).format('x');
        } else {
          claimQuery[queryName] = filter[queryKey];
        }
      }
    }
  }

  return claimQuery;
};

export const formatDisplayDateTime = (dateTime: number, format: string) => {
  return dateTime ? moment(dateTime).format(format) : '';
};

export const getFormattedErrorMessage = (err: IRequestError): string => {
  const errors = [];
  const backendErrorMessage =
    'A backend error has occurred. Please forward the following message to the Pegasus Engineering Team';

  if (err.response && err.response.status > 499) {
    errors.push(backendErrorMessage);
    errors.push(`[Status Code ${err.response.status}]`);
  }

  if (err.message) {
    errors.push(err.message);
  }

  const responseMessage = err?.response?.data.message || null;
  if (responseMessage) {
    errors.push(responseMessage);
  }

  if (!errors.length)
    errors.push(
      'An error has occurred. Unfortunately no additional information is available at this time.'
    );

  return errors.join(' - ');
};
